// Channel registry for Ad Studio Insights, shared by the backend
// and the frontend sync picker (packages/app imports this module directly).
// Keep it dependency-free so it stays safe to bundle into the app.
//
// Adding a channel = registering it here (label, entity vocabulary, metric
// availability) and registering its SQL spec in ./index.ts. The frontend
// allowlist, channel switcher, entity labels, and metric visibility all derive
// from this list.

export const INSIGHTS_METRIC_KEYS = [
  "spend",
  "impressions",
  "clicks",
  "reach",
  "ctr",
  "cpc",
  "cpm",
  "frequency",
  "cpa",
  "hookRate",
  "holdRate",
] as const;

export type InsightsMetricKey = (typeof INSIGHTS_METRIC_KEYS)[number];

/** Grouping levels exposed by the Insights API. Channels with different
 * native hierarchies (Google/TikTok ad groups, LinkedIn campaign groups) map
 * their levels onto these keys and relabel them via `entityLabels`. */
export type InsightsEntityType = "campaign" | "ad_set" | "ad";

/** Canonical entity statuses; each spec maps them to its channel's raw
 * status-column values, so the API, URLs, and UI never carry per-channel
 * vocabulary. */
export type InsightsStatusKey = "active" | "paused";

export interface InsightsChannel {
  /** Fivetran `source_type` identifying syncs of this channel. */
  sourceType: string;
  /** Channel switcher label. */
  label: string;
  /** Display label per grouping level ("Ad set", "Ad group", "Creative"). */
  entityLabels: Record<InsightsEntityType, string>;
  /** Metrics this channel's data can populate. Anything absent renders as
   * unavailable rather than zero. */
  availableMetrics: readonly InsightsMetricKey[];
  /** Whether the channel enumerates ad accounts for the account picker. */
  hasAccountPicker: boolean;
  /** Whether an ad's drilldown offers creative next steps (riff, refresh,
   * remix). Off where the ad level isn't a creative we can generate from. */
  hasSuggestedActions: boolean;
  /** LaunchDarkly flag gating the channel in the sync picker and in the
   * workspace's Insights syncs; absent means generally available. */
  featureFlag?: string;
  /** Caption shown above results at a grouping level whose data is known to
   * be incomplete (Google's PMax campaigns have no ads or ad groups). */
  groupingNotes?: Partial<Record<InsightsEntityType, string>>;
}

/** Single flag gating every post-Meta channel: its syncs are left out of the
 * workspace's Insights builds and pages until it is on. Meta predates the flag. */
const INSIGHTS_CHANNELS_FLAG = "adStudioInsights";

/** DV360's own gate. Its SQL spec exists, but nothing configures the
 * per-connector performance reports it reads (`line_item_performance` and
 * friends), so a real connector has no tables to query. Picking it can only
 * render an empty grid until that lands. */
export const DV360_CHANNEL_FLAG = "adStudioInsightsDv360";

export const META_CHANNEL: InsightsChannel = {
  sourceType: "facebook_ads",
  label: "Meta",
  entityLabels: { campaign: "Campaign", ad_set: "Ad set", ad: "Ad" },
  availableMetrics: INSIGHTS_METRIC_KEYS,
  hasAccountPicker: true,
  hasSuggestedActions: true,
};

export const TIKTOK_CHANNEL: InsightsChannel = {
  sourceType: "tiktok_ads",
  label: "TikTok",
  entityLabels: { campaign: "Campaign", ad_set: "Ad group", ad: "Ad" },
  availableMetrics: INSIGHTS_METRIC_KEYS,
  hasAccountPicker: true,
  hasSuggestedActions: true,
  featureFlag: INSIGHTS_CHANNELS_FLAG,
};

// Reach, frequency, and video-view metrics aren't in the synced stats
// tables yet, so they render as unavailable.
const GOOGLE_PMAX_NOTE =
  "Performance Max campaigns have no ads or ad groups, so their spend only appears when grouping by campaign.";

export const GOOGLE_CHANNEL: InsightsChannel = {
  sourceType: "google_ads",
  label: "Google",
  entityLabels: { campaign: "Campaign", ad_set: "Ad group", ad: "Ad" },
  availableMetrics: [
    "spend",
    "impressions",
    "clicks",
    "ctr",
    "cpc",
    "cpm",
    "cpa",
  ],
  hasAccountPicker: false,
  // Google's ad level is largely responsive text assets rather than a single
  // creative to riff on, so the drilldown offers no next steps yet.
  hasSuggestedActions: false,
  featureFlag: INSIGHTS_CHANNELS_FLAG,
  groupingNotes: { ad_set: GOOGLE_PMAX_NOTE, ad: GOOGLE_PMAX_NOTE },
};

export const LINKEDIN_CHANNEL: InsightsChannel = {
  sourceType: "linkedin_ads",
  label: "LinkedIn",
  entityLabels: {
    campaign: "Campaign group",
    ad_set: "Campaign",
    ad: "Creative",
  },
  availableMetrics: INSIGHTS_METRIC_KEYS,
  hasAccountPicker: false,
  hasSuggestedActions: true,
  featureFlag: INSIGHTS_CHANNELS_FLAG,
};

export const REDDIT_CHANNEL: InsightsChannel = {
  sourceType: "reddit_ads",
  label: "Reddit",
  entityLabels: { campaign: "Campaign", ad_set: "Ad group", ad: "Ad" },
  availableMetrics: [
    "spend",
    "impressions",
    "clicks",
    "ctr",
    "cpc",
    "cpm",
    "hookRate",
    "holdRate",
  ],
  hasAccountPicker: false,
  hasSuggestedActions: true,
  featureFlag: INSIGHTS_CHANNELS_FLAG,
};

export const DV360_CHANNEL = {
  sourceType: "google_display_and_video_360",
  label: "D&V 360",
  entityLabels: {
    campaign: "Campaign",
    ad_set: "Insertion order",
    ad: "Line item",
  },
  availableMetrics: [
    "spend",
    "impressions",
    "clicks",
    "ctr",
    "cpc",
    "cpm",
    "cpa",
  ],
  hasAccountPicker: true,
  // A line item is media placement config, not a creative to generate from.
  hasSuggestedActions: false,
  featureFlag: DV360_CHANNEL_FLAG,
} satisfies InsightsChannel;

export const YAHOO_CHANNEL: InsightsChannel = {
  sourceType: "yahoo_dsp",
  label: "Yahoo",
  entityLabels: { campaign: "Campaign", ad_set: "Line", ad: "Ad" },
  availableMetrics: INSIGHTS_METRIC_KEYS,
  hasAccountPicker: true,
  hasSuggestedActions: true,
  featureFlag: INSIGHTS_CHANNELS_FLAG,
};

export const INSIGHTS_CHANNELS: readonly InsightsChannel[] = [
  META_CHANNEL,
  TIKTOK_CHANNEL,
  GOOGLE_CHANNEL,
  LINKEDIN_CHANNEL,
  REDDIT_CHANNEL,
  DV360_CHANNEL,
  YAHOO_CHANNEL,
];

export function getInsightsChannel(
  sourceType: string | null | undefined,
): InsightsChannel | undefined {
  return INSIGHTS_CHANNELS.find((c) => c.sourceType === sourceType);
}
